import { EventEmitter, Type } from '@angular/core';
import { BackgroundJobComponent } from '../background-jobs/components/background-job/background-job.component';

export interface NotificationConfig<TComponent, TData> {
  hot?: boolean;
  colorSchema?: 'dark' | 'white';
  component?: Type<TComponent>;
  data?: TData;
  updater?: (instance: TComponent, data: TData) => void;
}

export interface NotificationRoomInfo {
  roomId: number;
  roomName: string;
  roomSpecific: boolean;
}

export class Notification<TComponent, TData> {
  hot: boolean;
  colorSchema?: 'dark' | 'white';
  readonly update: (data: TData) => void;
  readonly setInstance: (instance: TComponent) => void;
  public readonly component: Type<TComponent>;
  public readonly initialized = new EventEmitter();

  public instance: TComponent;

  constructor(options: NotificationConfig<TComponent, TData>) {
    this.colorSchema = options.colorSchema || 'white';
    this.component = options.component;
    this.hot = options.hot;

    this.setInstance = (instance: TComponent) => {
      this.instance = instance;
      this.initialized.emit();
      options.updater(instance, options.data);
    };

    this.update = (data: TData) => {
      if (options.updater) {
        options.updater(this.instance, data);
      }
    };
  }
}

interface NotificationNodeUpdated {
  operation: 'add' | 'delete' | 'make-read';
  value?: any;
  nodes: NotificationNode[];
}

export class NotificationNode {
  private static lastId = 0;
  readonly id: number;

  get hot(): boolean {
    if (this.data) {
      return this.data.hot;
    }
    return !!this.items.find((item) => item.hot);
  }

  set hot(value: boolean) {
    if (this.data) {
      this.data.hot = value;
    } else {
      this.items.forEach((item) => (item.hot = value));
    }
  }

  get totalCount(): number {
    return this.currentData ? 1 : this.itemsCalculator((item) => item.totalCount);
  }

  get hotItemsCount(): number {
    return this.currentData ? (this.currentData.hot ? 1 : 0) : this.itemsCalculator((item) => item.hotItemsCount);
  }

  get data(): Notification<any, any> {
    return this.currentData || (this.items.length === 1 ? this.items[0].currentData : null);
  }

  items: NotificationNode[] = [];
  private readonly change = new EventEmitter<NotificationNodeUpdated>();
  private readonly currentData: Notification<any, any>;

  static create(title: string, useAllItemsGroup?: boolean, allItemsGroupText?: string): NotificationNode {
    const allItemsRoot = useAllItemsGroup ? NotificationNode.create(allItemsGroupText, null, null) : null;
    return new NotificationNode(title, null, null, allItemsRoot, true);
  }

  private constructor(
    public readonly title: string,
    private readonly parent: NotificationNode,
    notificationOptions?: NotificationConfig<any, any>,
    private allItemsRoot?: NotificationNode,
    private readonly canNotBeDeletedByChild?: boolean,
    public readonly roomInfo?: NotificationRoomInfo,
  ) {
    this.id = ++NotificationNode.lastId;

    if (notificationOptions) {
      this.currentData = new Notification(notificationOptions);
    }

    if (this.allItemsRoot) {
      this.items = [this.allItemsRoot];

      this.change.subscribe((e: NotificationNodeUpdated) => {
        if (e.operation === 'add') {
          return e.nodes.forEach((node) =>
            node.change.subscribe((e2: NotificationNodeUpdated) => {
              if (e2.operation === 'add') {
                return this.allItemsRoot.items.unshift(...e2.nodes);
              }
              if (e2.operation === 'delete') {
                return e2.nodes.forEach((n) => this.allItemsRoot.deleteChild(n.id));
              }
            }),
          );
        }
        if (e.operation === 'delete') {
          return e.nodes.forEach((node) => node.change.unsubscribe());
        }
      });
    }
  }

  delete(): boolean {
    if (this.data?.instance instanceof BackgroundJobComponent && this.data?.instance.job.inProgress) {
      return false;
    }

    let res = false;

    if (this.parent) {
      res = this.parent.deleteChild(this.id);

      if (!this.parent.canNotBeDeletedByChild && this.parent.totalCount === 0) {
        this.parent.delete();
      }
    }

    return res;
  }

  deleteAll() {
    const hotItems = this.items.reduce<NotificationNode[]>((res, item) => {
      if (
        (item.hot || item.canNotBeDeletedByChild) &&
        !(item.data && item.data.instance instanceof BackgroundJobComponent && item.data.instance.job.inProgress)
      ) {
        res.push(item);
      }
      if (!item.hot) {
        item.deleteAll();
      }
      return res;
    }, []);

    this.items.splice(0, this.items.length, ...hotItems);
  }

  add(
    title: string,
    options?: NotificationConfig<any, any>,
    canNotBeDeletedByChild?: boolean,
    roomInfo?: NotificationRoomInfo,
  ): NotificationNode {
    const newNode = new NotificationNode(title, this, options, null, canNotBeDeletedByChild, roomInfo);
    if (!this.parent) {
      this.items.push(newNode);
    } else {
      this.items.splice(0, 0, newNode);
    }

    this.change.emit({ operation: 'add', nodes: [newNode] });
    return newNode;
  }

  private deleteChild(id: number): boolean {
    const index = this.items.findIndex((item) => item.id === id);
    if (index >= 0) {
      const node = this.items[index];
      this.items.splice(index, 1);
      this.change.emit({ operation: 'delete', nodes: [node] });
      return true;
    }

    let res = false;
    this.items.forEach((item) => (res = res || item.deleteChild(id)));
    return res;
  }

  getTotalCountForRoom(roomId: number): number {
    return this.currentData
      ? this.roomInfo && (this.roomInfo.roomId === roomId || !this.roomInfo.roomSpecific)
        ? 1
        : 0
      : this.itemsCalculator((item) => item.getTotalCountForRoom(roomId));
  }

  geHotItemsCountForRoom(roomId: number): number {
    return this.currentData
      ? this.currentData.hot && this.roomInfo && (this.roomInfo.roomId === roomId || !this.roomInfo.roomSpecific)
        ? 1
        : 0
      : this.itemsCalculator((item) => item.geHotItemsCountForRoom(roomId));
  }

  moveChildToTop(id: number) {
    const index = this.items.findIndex((item) => item.id === id);
    if (index >= 1) {
      const node = this.items[index];
      this.items.splice(index, 1);
      this.change.emit({ operation: 'delete', nodes: [node] });
      this.items.unshift(node);
      this.change.emit({ operation: 'add', nodes: [node] });
      return true;
    }
  }

  private itemsCalculator(countGetter: (item: NotificationNode) => number): number {
    let count = 0;

    this.items.forEach((item: NotificationNode, index: number) => {
      if (!(this.allItemsRoot && index === 0)) {
        count += countGetter(item);
      }
    });

    return count;
  }
}
